/**
 * §P.4 · La remesa del mes.
 *
 * **Es la operación que más miedo da**, y con razón: emite de una vez el recibo de
 * todas las familias del club. `BFF_BO.md` §7.2 pone tres pasos y dice que el
 * segundo no es opcional:
 *
 * ```
 * 1 · POST /billing-runs/preview  { period }   ← no escribe nada
 * 2 · Confirmación con el total y el número    ← esta pantalla
 * 3 · POST /billing-runs 🔒       { period }   ← con Idempotency-Key
 * ```
 *
 * ## La clave de idempotencia se genera al abrir el paso 1
 *
 * No al pulsar el botón del paso 3, y no fija. Las dos cosas están aprendidas:
 *
 * - **Fija fue un fallo real**, en el generador de clases. La segunda pulsación
 *   recibía **la respuesta guardada** y la pantalla decía «2 clases creadas» cuando
 *   no había creado ninguna: reportaba trabajo que no se hizo.
 * - **Al pulsar** tampoco sirve: dos pulsaciones seguidas por nervios son dos claves
 *   distintas, y ahí la red que queda es el `UNIQUE (academy_id, period)`, que
 *   responde `409`. Funciona, pero asusta sin motivo.
 *
 * Así que una clave por simulación: la misma que se enseñó es la que se lanza.
 *
 * ## Lo saltado se enseña antes de dejar lanzar
 *
 * Verlo después es una sorpresa desagradable. Y los dos motivos son distintos: uno
 * no hay que arreglarlo —ya está facturado— y el otro sí, emitiendo un recibo suelto.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fechaYHora } from '@/shared/ui/formato';
import {
  etiquetaDePeriodo,
  etiquetaDeSalto,
  explicacionDeSalto,
  periodoActual,
  periodosOfrecidos,
  type PlanDeRemesa,
  type Remesa as RemesaLanzada,
} from '@/club/facturacion/tipos';

export function Remesa({
  club,
  puedeLanzar,
  alVerRecibosDe,
}: {
  club: string;
  puedeLanzar: boolean;
  /** Del resultado al listado de recibos de ese mes. */
  alVerRecibosDe: (periodo: string) => void;
}) {
  const cache = useQueryClient();
  const [periodo, setPeriodo] = useState(() => periodoActual());
  const [plan, setPlan] = useState<PlanDeRemesa | null>(null);
  /*
   * La clave de la remesa, atada a **esta** simulación. Ver la cabecera: es lo que
   * hace que pulsar dos veces no invente un resultado, y que cada simulación nueva
   * sea una remesa nueva.
   */
  const [clave, setClave] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [yaLanzada, setYaLanzada] = useState<Problema | null>(null);
  const [resultado, setResultado] = useState<RemesaLanzada | null>(null);

  const historial = useQuery({
    queryKey: ['remesas', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['billing-runs'].get({ query: { limit: 12 } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return (data as unknown as { data: RemesaLanzada[] }).data;
    },
  });

  const simular = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        ['billing-runs'].preview.post({ period: periodo });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as PlanDeRemesa;
    },
    onSuccess: (p) => {
      setPlan(p);
      setProblema(null);
      setYaLanzada(null);
      // Una simulación, una clave.
      setClave(crypto.randomUUID());
    },
    onError: (e) => {
      setPlan(null);
      setProblema(aProblema(e));
    },
  });

  const lanzar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        ['billing-runs'].post(
          { period: periodo },
          { headers: clave ? { 'idempotency-key': clave } : {} }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as RemesaLanzada;
    },
    onSuccess: (r) => {
      setResultado(r);
      setPlan(null);
      setClave(null);
      void cache.invalidateQueries({ queryKey: ['remesas', club] });
      void cache.invalidateQueries({ queryKey: ['recibos', club] });
      void cache.invalidateQueries({ queryKey: ['morosos', club] });
    },
    onError: (e) => {
      const p = aProblema(e);
      // `409 billing_run_exists` no es un error que pintar en rojo: la respuesta a
      // «¿se lanzó o no?» está en el historial, justo debajo.
      if (p.code === 'billing_run_exists') setYaLanzada(p);
      else setProblema(p);
    },
  });

  // ── El resultado, después de lanzar ──────────────────────────────────────
  if (resultado) {
    return (
      <section>
        <h1>Remesa de {etiquetaDePeriodo(resultado.period)}</h1>

        <div className="aviso aviso-calma" role="status">
          <strong>
            {resultado.invoices_created === 1
              ? 'Se ha emitido 1 recibo'
              : `Se han emitido ${resultado.invoices_created} recibos`}{' '}
            por {euros(resultado.total_cents)}.
          </strong>{' '}
          {resultado.invoices_skipped > 0 &&
            `${resultado.invoices_skipped} cuotas se quedaron fuera.`}
        </div>

        {resultado.error && (
          <div className="aviso" role="status">
            <strong>La remesa terminó con un error.</strong> {resultado.error}
          </div>
        )}

        <div className="acciones">
          <button
            type="button"
            onClick={() => alVerRecibosDe(resultado.period)}
          >
            Ver los recibos de este mes
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => setResultado(null)}
          >
            Volver
          </button>
        </div>
      </section>
    );
  }

  // ── El paso 2: la confirmación ───────────────────────────────────────────
  if (plan) {
    const cuantos = plan.invoices.length;

    return (
      <section>
        <h1>Remesa de {etiquetaDePeriodo(plan.period)}</h1>

        {/* La frase que el club tiene que poder leer antes de pulsar. */}
        <div className="aviso" role="status">
          <strong>
            {cuantos === 0
              ? 'No hay nada que emitir este mes.'
              : cuantos === 1
                ? `Se va a emitir 1 recibo por ${euros(plan.total_cents)}.`
                : `Se van a emitir ${cuantos} recibos por ${euros(plan.total_cents)}.`}
          </strong>{' '}
          {cuantos > 0 &&
            'Todavía no se ha emitido nada: esto es una simulación.'}
        </div>

        {/*
          Los saltados **antes** de dejar lanzar, con motivo y con nombre. Doce filas
          que no aparecen son un descuadre que nadie detecta hasta que llama una
          familia.
        */}
        {plan.skipped.length > 0 && (
          <>
            <h2 className="seccion">
              {plan.skipped.length === 1
                ? 'Una cuota se queda fuera'
                : `${plan.skipped.length} cuotas se quedan fuera`}
            </h2>
            <ul className="saltados">
              {plan.skipped.map((s) => (
                <li key={s.subscription_id}>
                  <span>
                    <strong>{s.student_name}</strong>{' '}
                    <span className="tenue">· {s.payer_name}</span>
                  </span>
                  <span className="saltado-motivo">
                    {etiquetaDeSalto(s.reason)}
                    <span className="tenue">
                      {' '}
                      {explicacionDeSalto(s.reason)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        {cuantos > 0 && (
          <>
            <h2 className="seccion">Lo que se va a emitir</h2>
            <div className="tabla-marco">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Paga</th>
                    <th>Por</th>
                    <th className="num">Base</th>
                    <th className="num">Descuentos</th>
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.invoices.map((i) => (
                    <tr key={i.payer_id}>
                      <td>{i.payer_name}</td>
                      {/* Una línea por hijo: es lo que hace que un recibo de 3 hijos se entienda. */}
                      <td className="tenue">
                        {i.lines
                          .map((l) => `${l.student_name} (${l.fee_plan})`)
                          .join(', ')}
                      </td>
                      <td className="num">{euros(i.subtotal_cents)}</td>
                      <td className="num">
                        {i.discount_cents === 0
                          ? '—'
                          : `−${euros(i.discount_cents)}`}
                      </td>
                      <td className="num">
                        <strong>{euros(i.total_cents)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/*
          Un segundo intento del mismo mes: no en rojo, y con el camino al historial,
          que es donde está la respuesta a «¿se lanzó o no?».
        */}
        {yaLanzada && (
          <div className="aviso" role="status">
            <strong>{yaLanzada.title}</strong> {yaLanzada.detail}
            <div className="hueco-acciones">
              <button
                type="button"
                onClick={() => {
                  setPlan(null);
                  setYaLanzada(null);
                }}
              >
                Ver el historial
              </button>
            </div>
          </div>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          {cuantos > 0 && puedeLanzar && (
            <button
              type="button"
              disabled={lanzar.isPending}
              onClick={() => lanzar.mutate()}
            >
              {lanzar.isPending
                ? 'Emitiendo…'
                : `Emitir ${cuantos === 1 ? 'el recibo' : `los ${cuantos} recibos`}`}
            </button>
          )}
          <button
            type="button"
            className="btn-plano"
            disabled={lanzar.isPending}
            onClick={() => {
              setPlan(null);
              setClave(null);
              setYaLanzada(null);
            }}
          >
            Dejarlo
          </button>
        </div>

        <p className="tenue">
          Los importes los calcula el servidor al emitir, no esta pantalla. Si
          algo cambia en los próximos minutos —un descuento que caduca, una
          cuota que se pausa—, el recibo saldrá con lo correcto y no con lo que
          se ve aquí.
        </p>
      </section>
    );
  }

  // ── El paso 1: elegir el mes ─────────────────────────────────────────────
  return (
    <section>
      <header className="titulo-fila">
        <h1>Remesa</h1>
      </header>

      <p className="tenue">
        Emite de una vez el recibo del mes de todas las familias con cuota
        activa. Antes de emitir nada se ve qué se va a emitir.
      </p>

      <div className="rejilla">
        <label>
          Mes que se cobra
          <select
            value={periodo}
            onChange={(e) => {
              setPeriodo(e.target.value);
              setPlan(null);
              setClave(null);
            }}
          >
            {/* Sin meses futuros: ver `periodosOfrecidos`. */}
            {periodosOfrecidos().map((p) => (
              <option key={p} value={p}>
                {etiquetaDePeriodo(p)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {problema && <PanelError problema={problema} />}

      <div className="acciones">
        <button
          type="button"
          disabled={simular.isPending}
          onClick={() => simular.mutate()}
        >
          {simular.isPending ? 'Calculando…' : 'Ver qué se va a emitir'}
        </button>
      </div>

      <h2 className="seccion">Remesas anteriores</h2>

      {historial.isPending && <Cargando filas={3} etiqueta="Cargando" />}

      {historial.isError && (
        <PanelError
          problema={aProblema(historial.error)}
          reintentar={() => void historial.refetch()}
        />
      )}

      {historial.data?.length === 0 && (
        <Vacio
          titulo="Todavía no se ha lanzado ninguna remesa"
          explicacion="La primera emite los recibos del mes en curso. Los importes salen de la cuota de cada alumno, así que conviene tenerlas puestas antes."
        />
      )}

      {historial.data && historial.data.length > 0 && (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Mes</th>
                <th className="num">Recibos</th>
                <th className="num">Fuera</th>
                <th className="num">Total</th>
                <th>Lanzada</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {historial.data.map((r) => (
                <tr key={r.id}>
                  <td>{etiquetaDePeriodo(r.period)}</td>
                  <td className="num">{r.invoices_created}</td>
                  <td className="num tenue">
                    {r.invoices_skipped === 0 ? '—' : r.invoices_skipped}
                  </td>
                  <td className="num">{euros(r.total_cents)}</td>
                  <td className="tenue">
                    {fechaYHora(r.finished_at ?? r.created_at)}
                    {r.status === 'failed' && (
                      <span className="pastilla pastilla-mal">Falló</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => alVerRecibosDe(r.period)}
                    >
                      Ver recibos
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
